/**
 * Shading state — how a mesh's topology is *interpreted* by the renderer,
 * deliberately separate from the topology that describes its *shape*.
 *
 * Phase 2, Milestone D. Before this, `buildGeometryFromTopology` had exactly
 * one rule: share a render vertex only between faces whose flat normals agree
 * within `RENDER_NORMAL_EPSILON` (1e-6, about 0.08°). That is an
 * exact-coplanarity test, so the behaviour was strictly binary — coplanar or
 * hard, with nothing in between and no way to ask for anything else.
 *
 * That default is right for hard-surface work and stays the default. The gap
 * was that it was the only option: a mesh that is smooth by construction still
 * rendered as facets, which no amount of added topology could fix because
 * shading is engine-owned.
 *
 * Modes:
 *   flat   — every genuinely-coplanar corner shares; everything else is hard.
 *            Identical to pre-Milestone-D behaviour, byte for byte.
 *   smooth — all faces meeting at a topology vertex share one normal.
 *   auto   — share below `angleDeg`, split above it.
 *
 * `flat` and `smooth` are named modes rather than angles a caller has to
 * remember, but all three resolve to one angle threshold so there is a single
 * grouping rule rather than three similar-looking ones.
 */

/** Matches `render.js`; re-declared here so the threshold formula is self-contained. */
const RENDER_NORMAL_EPSILON = 1e-6;

export const SHADING_MODES = Object.freeze(['flat', 'smooth', 'auto']);

/**
 * Owner decision, Phase 2 §7.1: 30° is the default and is explicitly *not*
 * sacred. What matters is that the angle is accepted and persisted per object,
 * so this default can move later without changing any existing object.
 */
export const DEFAULT_AUTO_ANGLE_DEG = 30;

export const FLAT_SHADING = Object.freeze({ mode: 'flat', angleDeg: 0 });

/**
 * Canonicalize shading state arriving from anywhere — a command argument, a
 * replayed log entry, a restored snapshot, or an object that predates this
 * milestone and has none.
 *
 * Absent shading is `flat`, which is what makes every mesh created before
 * Milestone D keep rendering exactly as it did.
 */
export function normalizeShading(raw) {
  if (raw === undefined || raw === null) return { ...FLAT_SHADING };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`shading must be an object, got ${JSON.stringify(raw)}`);
  }
  const mode = raw.mode ?? 'flat';
  if (!SHADING_MODES.includes(mode)) {
    throw new Error(`shading.mode must be one of ${SHADING_MODES.join(', ')}, got ${JSON.stringify(raw.mode)}`);
  }

  // flat and smooth are the endpoints. Accepting an angle alongside them would
  // create two ways to say the same thing and two ways for a hash to disagree
  // about identical state, so they canonicalise to their own fixed angles.
  if (mode === 'flat') return { mode: 'flat', angleDeg: 0 };
  if (mode === 'smooth') return { mode: 'smooth', angleDeg: 180 };

  const angleDeg = raw.angleDeg ?? DEFAULT_AUTO_ANGLE_DEG;
  if (typeof angleDeg !== 'number' || !Number.isFinite(angleDeg)) {
    throw new Error(`shading.angleDeg must be a finite number, got ${JSON.stringify(raw.angleDeg)}`);
  }
  if (angleDeg < 0 || angleDeg > 180) {
    throw new Error(`shading.angleDeg must be between 0 and 180, got ${angleDeg}`);
  }
  return { mode: 'auto', angleDeg };
}

/** True when this shading is the default, and so need not be stored or serialized. */
export function isDefaultShading(shading) {
  const normalized = normalizeShading(shading);
  return normalized.mode === 'flat';
}

/**
 * The minimum dot product two face normals must exceed to share a render
 * vertex.
 *
 * At `angleDeg` 0 this is exactly `1 - RENDER_NORMAL_EPSILON`, which is
 * precisely the pre-Milestone-D test — that identity is what makes `flat`
 * byte-identical to previous behaviour rather than merely similar to it, and
 * it is asserted directly in the tests.
 */
export function shadingMinDot(shading) {
  const { angleDeg } = normalizeShading(shading);
  return Math.cos((angleDeg * Math.PI) / 180) - RENDER_NORMAL_EPSILON;
}
